import React, { useState, useRef } from 'react';
import { Send, Smile, Image, AtSign } from 'lucide-react';
import { showToast } from '../../utils/toast';

interface ReplyComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  currentUser: any;
}

const ReplyComposer: React.FC<ReplyComposerProps> = ({
  value,
  onChange,
  onSubmit,
  isSubmitting,
  currentUser
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showTagMenu, setShowTagMenu] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Mock experts for tagging
  const experts = [
    { id: 'exp1', name: 'Dr. Ama Osei', specialty: 'General Medicine' },
    { id: 'exp2', name: 'Dr. Kwaku Mensah', specialty: 'Mental Health' },
    { id: 'exp3', name: 'Dr. Akosua Frimpong', specialty: 'Nutrition' }
  ];
  
  // Mock emoji picker
  const emojis = ['😊', '👍', '❤️', '🙏', '👏', '🤔', '💡', '✨', '🌟', '💪'];
  
  const handleEmojiClick = (emoji: string) => {
    onChange(value + emoji);
    setShowEmojiPicker(false);
    
    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  const handleTagExpert = (expert: typeof experts[0]) => {
    onChange(value + `@${expert.name} `);
    setShowTagMenu(false);
    
    // Focus back on input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  const handleImageUpload = () => {
    // In a real app, this would open a file picker
    showToast.system.info('Image upload coming soon!');
  };
  
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };
  
  return (
    <div className="flex items-center space-x-2">
      {/* User Avatar */}
      <div className="avatar-sm">
        {currentUser?.full_name?.charAt(0) || 'U'}
      </div>
      
      {/* Input Area */}
      <div className="relative flex-1">
        <input
          id="reply-input"
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Add to the discussion..."
          className="input-base w-full pr-24 dark:bg-gray-800 dark:border-gray-700"
          disabled={isSubmitting}
        />
        
        {/* Action Buttons */}
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {/* Emoji Picker */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Smile size={18} />
            </button>
            
            {showEmojiPicker && (
              <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 z-10 animate-scale-in">
                <div className="grid grid-cols-5 gap-2">
                  {emojis.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => handleEmojiClick(emoji)}
                      className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-lg"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Tag Expert */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTagMenu(!showTagMenu)}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <AtSign size={18} />
            </button>
            
            {showTagMenu && (
              <div className="absolute bottom-full right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 z-10 w-60 animate-scale-in">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 px-2">Tag an expert:</p>
                {experts.map((expert) => (
                  <button
                    key={expert.id}
                    onClick={() => handleTagExpert(expert)}
                    className="flex items-center space-x-2 w-full p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-left"
                  >
                    <div className="w-6 h-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center text-green-800 dark:text-green-200 text-xs font-bold">
                      {expert.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{expert.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{expert.specialty}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Image Upload */}
          <button
            type="button"
            onClick={handleImageUpload}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Image size={18} />
          </button>
        </div>
      </div>
      
      {/* Send Button */}
      <button
        onClick={onSubmit}
        disabled={!value.trim() || isSubmitting}
        className={`p-2 rounded-full ${
          !value.trim() || isSubmitting
            ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
            : 'bg-green-600 text-white hover:bg-green-700 transition-colors'
        }`}
      >
        <Send size={18} />
      </button>
    </div>
  );
};

export default ReplyComposer;